<template>
    <b-card class="requests-navbar mb-1" body-class="requests-navbar__body">
        <div
            class="requests-toolbar"
            :class="{
                'requests-toolbar--filters-open': mobileFiltersOpen,
                'requests-toolbar--has-favorites': favoriteRequestTypes.length
            }"
        >
            <div class="requests-toolbar__title">
                <h5 class="mb-0">Заявки</h5>
            </div>

            <b-input-group
                size="sm"
                class="requests-toolbar__search"
            >
                <b-input-group-prepend>
                    <b-input-group-text><i class="fas fa-search"></i></b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                    v-model="localFilter"
                    placeholder="Поиск: №, описание, инициатор..."
                    @input="onFilterChange"
                    @keyup.enter="closeMobileFilters"
                />
                <b-input-group-append>
                    <b-button
                        variant="outline-secondary"
                        :disabled="!localFilter"
                        @click="onClearFilter"
                        aria-label="Очистить поиск"
                        title="Очистить"
                    >
                        <i class="fas fa-times d-md-none"></i>
                        <span class="d-none d-md-inline">Очистить</span>
                    </b-button>
                </b-input-group-append>
            </b-input-group>

            <div v-if="favoriteRequestTypes.length" class="requests-toolbar__favorites">
                <div
                    class="requests-favorite-types"
                    @wheel="scrollHorizontalOnWheel"
                >
                    <div
                        v-for="type in favoriteRequestTypes"
                        :key="type.value"
                        class="requests-favorite-type"
                        :class="{ 'requests-favorite-type--active': String(requestTypeFilter || 'all') === String(type.value) }"
                    >
                        <button
                            type="button"
                            class="requests-favorite-type__filter"
                            :title="`Фильтр: ${type.text}`"
                            @click="setFavoriteRequestTypeFilter(type.value)"
                        >
                            <i
                                :class="String(requestTypeFilter || 'all') === String(type.value) ? 'fas fa-check' : (type.isCategoryHeader ? type.icon : 'fas fa-filter')"
                                aria-hidden="true"
                            ></i>
                            <span>{{ type.text }}</span>
                        </button>
                        <button
                            type="button"
                            class="requests-favorite-type__remove"
                            :title="`Убрать из избранного: ${type.text}`"
                            @click="toggleFavoriteRequestType(type.value)"
                        >
                            <i class="fas fa-star" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div class="requests-toolbar__object">
                <v-select
                    v-model="localBuildingProcessFilter"
                    :options="buildingProcessSelectOptions"
                    label="text"
                    :reduce="option => option.value"
                    :clearable="false"
                    :searchable="true"
                    :select-on-tab="true"
                    placeholder="Объект"
                    class="requests-object-filter"
                    :class="{ 'requests-object-filter--active': isBuildingProcessFilterActive }"
                    @input="onBuildingProcessFilterChange"
                >
                    <template #selected-option="option">
                        <span class="requests-object-filter__selected">
                            {{ option.text }}
                        </span>
                    </template>
                    <template #no-options>
                        Объект не найден
                    </template>
                </v-select>
            </div>

            <div class="requests-toolbar__type">
                <v-select
                    v-model="localRequestTypeFilter"
                    :options="requestTypeOptions"
                    label="text"
                    :reduce="option => option.value"
                    :clearable="false"
                    :searchable="true"
                    :select-on-tab="true"
                    placeholder="Тип заявки"
                    class="requests-type-filter"
                    :class="{ 'requests-type-filter--active': isRequestTypeFilterActive }"
                    @input="onRequestTypeFilterChange"
                >
                    <template #option="option">
                        <div
                            v-if="option.isCategoryHeader"
                            class="requests-type-filter__category"
                        >
                            <button
                                type="button"
                                class="requests-type-filter__favorite"
                                :class="{ 'requests-type-filter__favorite--active': option.isFavorite }"
                                :title="option.isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'"
                                @mousedown.stop.prevent="toggleFavoriteRequestType(option.value)"
                                @click.stop.prevent="stopFavoriteClick"
                                @keydown.enter.stop.prevent="toggleFavoriteRequestType(option.value)"
                                @keydown.space.stop.prevent="toggleFavoriteRequestType(option.value)"
                            >
                                <i class="fas fa-star" aria-hidden="true"></i>
                            </button>
                            <i :class="[option.icon, 'requests-type-filter__category-icon']" aria-hidden="true"></i>
                            <span class="requests-type-filter__option-text">{{ option.text }}</span>
                        </div>
                        <div v-else class="requests-type-filter__option">
                            <button
                                v-if="option.value !== 'all'"
                                type="button"
                                class="requests-type-filter__favorite"
                                :class="{ 'requests-type-filter__favorite--active': option.isFavorite }"
                                :title="option.isFavorite ? 'Убрать из избранного' : 'Добавить в избранное'"
                                @mousedown.stop.prevent="toggleFavoriteRequestType(option.value)"
                                @click.stop.prevent="stopFavoriteClick"
                                @keydown.enter.stop.prevent="toggleFavoriteRequestType(option.value)"
                                @keydown.space.stop.prevent="toggleFavoriteRequestType(option.value)"
                            >
                                <i class="fas fa-star" aria-hidden="true"></i>
                            </button>
                            <span class="requests-type-filter__option-text">{{ option.text }}</span>
                        </div>
                    </template>
                    <template #selected-option="option">
                        <span class="requests-type-filter__selected">
                            <i
                                v-if="option.isCategoryHeader"
                                :class="[option.icon, 'requests-type-filter__selected-category-icon']"
                                aria-hidden="true"
                            ></i>
                            <i
                                v-if="option.value !== 'all' && option.isFavorite"
                                class="fas fa-star requests-type-filter__selected-star"
                                aria-hidden="true"
                            ></i>
                            {{ option.text }}
                        </span>
                    </template>
                    <template #no-options>
                        Тип не найден
                    </template>
                </v-select>
            </div>

            <div class="requests-toolbar__filters">
                <div
                    class="requests-filter-chips"
                    @wheel="scrollHorizontalOnWheel"
                >
                    <button
                        v-for="option in statusFilterOptions"
                        :key="option.value"
                        type="button"
                        class="requests-filter-chip"
                        :class="[
                            `requests-filter-chip--${option.variant}`,
                            { 'requests-filter-chip--active': statusFilter === option.value }
                        ]"
                        @click="setStatusFilter(option.value)"
                    >
                        <i
                            v-if="statusFilter === option.value"
                            class="fas fa-check requests-filter-chip__check"
                            aria-hidden="true"
                        ></i>
                        <i v-if="option.icon" :class="[option.icon, 'mr-1']"></i>
                        {{ option.text }}
                    </button>
                </div>

                <b-form-select
                    class="requests-toolbar__recurrence"
                    :class="{ 'border-primary text-primary': recurrenceFilter }"
                    size="sm"
                    aria-label="Регулярность заявок"
                    title="Регулярность заявок"
                    :value="recurrenceFilter"
                    :options="recurrenceOptions"
                    @change="$emit('recurrenceFilterChange', $event)"
                />
                <div class="requests-toolbar__sort">
                    <b-form-select
                        v-model="localSortBy"
                        :options="sortOptions"
                        size="sm"
                        @change="onSortByChange"
                    />
                    <b-button
                        variant="outline-secondary"
                        size="sm"
                        @click="toggleSortDesc"
                        class="sort-dir-btn"
                        :title="localSortDesc ? 'По убыванию' : 'По возрастанию'"
                    >
                        <i :class="localSortDesc ? 'fas fa-sort-amount-down' : 'fas fa-sort-amount-up'"></i>
                    </b-button>
                </div>
            </div>

            <div v-if="activeFilterChips.length" class="requests-active-filters">
                <span class="requests-active-filters__label">
                    <i class="fas fa-filter"></i>
                    Включены фильтры:
                </span>
                <button
                    v-for="chip in activeFilterChips"
                    :key="chip.key"
                    type="button"
                    class="requests-active-filter"
                    :class="`requests-active-filter--${chip.variant}`"
                    :title="`Снять фильтр: ${chip.text}`"
                    @click="clearFilterChip(chip.key)"
                >
                    <i class="fas fa-check" aria-hidden="true"></i>
                    <span class="requests-active-filter__text">{{ chip.text }}</span>
                    <i class="fas fa-times requests-active-filter__remove" aria-hidden="true"></i>
                </button>
                <button
                    type="button"
                    class="requests-active-filters__reset"
                    @click="clearAllFilters"
                >
                    Сбросить все
                </button>
            </div>

            <div class="requests-toolbar__actions">
                <b-button
                    variant="outline-secondary"
                    size="sm"
                    class="filters-toggle d-md-none"
                    :class="{ 'filters-toggle--active': hasActiveFilters }"
                    @click="toggleMobileFilters"
                    aria-label="Статусы и сортировка"
                    title="Статусы и сортировка"
                    :aria-expanded="mobileFiltersOpen ? 'true' : 'false'"
                >
                    <i class="fas fa-sliders-h"></i>
                    <span v-if="hasActiveFilters" class="filters-toggle__dot"></span>
                </b-button>

                <b-button
                    v-if="$can('read', 'requestTypes')"
                    variant="outline-primary"
                    size="sm"
                    to="/requests/types"
                    class="request-types-btn"
                    aria-label="Типы заявок"
                    title="Типы заявок"
                >
                    <i class="fas fa-cogs"></i>
                    <span class="d-none d-sm-inline">Типы заявок</span>
                    <span class="d-sm-none">Типы</span>
                </b-button>

                <b-button
                    v-if="canCreateRequest"
                    variant="primary"
                    size="sm"
                    @click="createRequest"
                    class="create-btn"
                    aria-label="Создать заявку"
                    title="Создать заявку"
                >
                    <i class="fas fa-plus mr-1"></i>
                    <span>Создать заявку</span>
                </b-button>
            </div>
        </div>
    </b-card>
</template>

<script>
import { REQUEST_CATEGORIES, normalizeRequestCategory } from '@/constants/requestCategories';

const FAVORITE_REQUEST_TYPES_STORAGE_PREFIX = 'argus:requests:favorite-type-filters';
const REQUEST_CATEGORY_FILTER_PREFIX = 'category:';

export default {
    name: "RequestsNavbar",
    props: {
        sortOptions: { type: Array, default: () => [] },
        sortBy: { type: String, default: 'createdAt' },
        sortDesc: { type: Boolean, default: true },
        filter: { type: String, default: '' },
        statusFilter: { type: String, default: 'all' },
        requestTypeFilter: { type: [String, Number], default: 'all' },
        recurrenceFilter: { type: String, default: '' },
        buildingProcessFilter: { type: [String, Number], default: 'all' },
        requestTypes: { type: Array, default: () => [] },
        buildingProcessOptions: { type: Array, default: () => [] },
        badgeFilterOptions: { type: Array, default: () => [] },
        canCreateRequest: { type: Boolean, default: false }
    },
    data() {
        return {
            localFilter: this.filter,
            localSortBy: this.sortBy,
            localSortDesc: this.sortDesc,
            localRequestTypeFilter: String(this.requestTypeFilter || 'all'),
            localBuildingProcessFilter: String(this.buildingProcessFilter || 'all'),
            mobileFiltersOpen: false,
            favoriteRequestTypeIds: []
        };
    },
    computed: {
        currentUser() {
            return this.$store.getters.currentUser;
        },
        favoriteStorageKey() {
            const user = this.currentUser || {};
            const userId = user.userId || user.user_id || user.id;
            if (!userId) return null;
            const company = user.company || 'default';
            return `${FAVORITE_REQUEST_TYPES_STORAGE_PREFIX}:${company}:${userId}`;
        },
        favoriteRequestTypeSet() {
            return this.favoriteRequestTypeIds.reduce((acc, id) => {
                acc[String(id)] = true;
                return acc;
            }, {});
        },
        statusFilterOptions() {
            const options = [{ value: 'all', text: 'Все', variant: 'secondary' }];
            return options.concat((this.badgeFilterOptions || []).filter(option => option && option.value));
        },
        requestTypeFlatOptions() {
            return (this.requestTypes || [])
                .filter(type => type && type.id)
                .map(type => ({
                    value: String(type.id),
                    text: type.name,
                    category: normalizeRequestCategory(type.category),
                    isFavorite: this.isFavoriteRequestType(type.id)
                }));
        },
        availableFavoriteRequestTypeValues() {
            const available = {};

            this.requestTypeFlatOptions.forEach(option => {
                available[option.value] = true;
            });

            const categoriesWithTypes = this.requestTypeFlatOptions.reduce((acc, option) => {
                if (option.category) acc[option.category] = true;
                return acc;
            }, {});

            REQUEST_CATEGORIES.forEach(category => {
                if (!categoriesWithTypes[category.value]) return;
                available[`${REQUEST_CATEGORY_FILTER_PREFIX}${category.value}`] = true;
            });

            return available;
        },
        requestTypeOptions() {
            const options = [{ value: 'all', text: 'Все типы заявок' }];
            const byCategory = this.requestTypeFlatOptions.reduce((acc, option) => {
                if (!acc[option.category]) acc[option.category] = [];
                acc[option.category].push(option);
                return acc;
            }, {});

            REQUEST_CATEGORIES.forEach(category => {
                const categoryOptions = byCategory[category.value] || [];
                if (!categoryOptions.length) return;

                const categoryValue = `${REQUEST_CATEGORY_FILTER_PREFIX}${category.value}`;
                options.push({
                    value: categoryValue,
                    text: category.text,
                    icon: category.icon,
                    isCategoryHeader: true,
                    isFavorite: this.isFavoriteRequestType(categoryValue)
                });
                options.push(...categoryOptions);
            });

            return options;
        },
        buildingProcessSelectOptions() {
            const options = [{ value: 'all', text: 'Все объекты' }];
            const seen = {};

            (this.buildingProcessOptions || []).forEach(process => {
                if (!process) return;
                const rawValue = process.value || process.id || process.building_process_id;
                const value = rawValue ? String(rawValue) : '';
                if (!value || value === 'all' || seen[value]) return;

                const text = process.text || process.object_name || process.name || `Объект #${value}`;
                seen[value] = true;
                options.push({ value, text });
            });

            return options;
        },
        favoriteRequestTypes() {
            return this.requestTypeOptions.filter(option =>
                option
                && option.value !== 'all'
                && this.favoriteRequestTypeSet[String(option.value)]
            );
        },
        recurrenceOptions() {
            return [{value: '', text: 'Любая регулярность'}, {value: 'single', text: 'Разовые'},
                {value: 'template', text: 'Серии'}, {value: 'occurrence', text: 'Повторения серий'}];
        },
        hasActiveFilters() {
            return Boolean(this.localFilter)
                || Boolean(this.recurrenceFilter)
                || this.statusFilter !== 'all'
                || String(this.requestTypeFilter || 'all') !== 'all'
                || String(this.buildingProcessFilter || 'all') !== 'all';
        },
        isRequestTypeFilterActive() {
            return String(this.requestTypeFilter || 'all') !== 'all';
        },
        isBuildingProcessFilterActive() {
            return String(this.buildingProcessFilter || 'all') !== 'all';
        },
        activeStatusFilterOption() {
            const fallbackOptions = {
                fulfillment_review: { text: 'Требует разбора', variant: 'warning' },
                overdue: { text: 'Просрочена', variant: 'danger' },
                deadline_today: { text: 'Сегодня', variant: 'info' },
                deadline_remaining: { text: 'Осталось', variant: 'warning' },
                urgent: { text: 'Срочно', variant: 'danger' },
                high_priority: { text: 'Высокий приоритет', variant: 'warning' },
                draft: { text: 'Черновик', variant: 'secondary' },
                submitted: { text: 'Отправлена', variant: 'info' },
                in_review: { text: 'На рассмотрении', variant: 'warning' },
                returned: { text: 'Возвращена', variant: 'warning' },
                approved: { text: 'Согласована', variant: 'success' },
                awaiting_executor: { text: 'Ожидание исполнителя', variant: 'info' },
                in_progress: { text: 'В работе', variant: 'primary' },
                partially_completed: { text: 'Частично выполнена', variant: 'warning' },
                awaiting_acceptance: { text: 'Ожидает приемки', variant: 'info' },
                partially_accepted: { text: 'Частично принята', variant: 'info' },
                completed: { text: 'Выполнена', variant: 'success' },
                rejected: { text: 'Отклонена', variant: 'danger' },
                cancelled: { text: 'Отменена', variant: 'dark' }
            };
            const option = this.statusFilterOptions.find(item => item.value === this.statusFilter);
            return option || (fallbackOptions[this.statusFilter] ? {
                value: this.statusFilter,
                ...fallbackOptions[this.statusFilter]
            } : null);
        },
        activeRequestTypeOption() {
            return this.requestTypeOptions.find(option => String(option.value) === String(this.requestTypeFilter)) || null;
        },
        activeBuildingProcessOption() {
            return this.buildingProcessSelectOptions.find(option =>
                String(option.value) === String(this.buildingProcessFilter)
            ) || null;
        },
        activeFilterChips() {
            const chips = [];
            const recurrence = this.recurrenceOptions.find(option => option.value === this.recurrenceFilter);
            if (this.recurrenceFilter && recurrence) {
                chips.push({key: 'recurrence', text: recurrence.text, variant: 'primary'});
            }
            if (this.localFilter) {
                chips.push({
                    key: 'search',
                    text: `Поиск: ${this.localFilter}`,
                    variant: 'primary'
                });
            }
            if (this.statusFilter !== 'all' && this.activeStatusFilterOption) {
                chips.push({
                    key: 'status',
                    text: this.activeStatusFilterOption.text,
                    variant: this.activeStatusFilterOption.variant || 'secondary'
                });
            }
            if (this.isRequestTypeFilterActive && this.activeRequestTypeOption) {
                const prefix = this.activeRequestTypeOption.isCategoryHeader ? 'Категория' : 'Тип';
                chips.push({
                    key: 'requestType',
                    text: `${prefix}: ${this.activeRequestTypeOption.text}`,
                    variant: 'warning'
                });
            }
            if (this.isBuildingProcessFilterActive && this.activeBuildingProcessOption) {
                chips.push({
                    key: 'buildingProcess',
                    text: `Объект: ${this.activeBuildingProcessOption.text}`,
                    variant: 'info'
                });
            }
            return chips;
        }
    },
    methods: {
        normalizeFavoriteRequestTypeIds(ids, filterAvailable = true) {
            const available = this.availableFavoriteRequestTypeValues;
            const shouldFilterAvailable = filterAvailable && Boolean(Object.keys(available).length);
            const seen = {};
            return (Array.isArray(ids) ? ids : [])
                .map(id => String(id))
                .filter(id => {
                    if (!id || id === 'all' || seen[id]) return false;
                    if (id.indexOf(REQUEST_CATEGORY_FILTER_PREFIX) === 0) {
                        const category = id.slice(REQUEST_CATEGORY_FILTER_PREFIX.length);
                        if (!REQUEST_CATEGORIES.some(item => item.value === category)) return false;
                    }
                    if (shouldFilterAvailable && !available[id]) return false;
                    seen[id] = true;
                    return true;
                });
        },
        loadFavoriteRequestTypes() {
            if (!this.favoriteStorageKey) {
                this.favoriteRequestTypeIds = [];
                return;
            }
            if (typeof window === 'undefined' || !window.localStorage) return;
            try {
                const raw = window.localStorage.getItem(this.favoriteStorageKey);
                const parsed = raw ? JSON.parse(raw) : [];
                this.favoriteRequestTypeIds = this.normalizeFavoriteRequestTypeIds(parsed, false);
                this.pruneFavoriteRequestTypes();
            } catch (e) {
                this.favoriteRequestTypeIds = [];
            }
        },
        persistFavoriteRequestTypes() {
            if (!this.favoriteStorageKey || typeof window === 'undefined' || !window.localStorage) return;
            try {
                window.localStorage.setItem(this.favoriteStorageKey, JSON.stringify(this.favoriteRequestTypeIds));
            } catch (e) { /* ignore */ }
        },
        pruneFavoriteRequestTypes() {
            if (!this.requestTypes || !this.requestTypes.length) return;
            const normalized = this.normalizeFavoriteRequestTypeIds(this.favoriteRequestTypeIds);
            if (normalized.join('|') !== this.favoriteRequestTypeIds.join('|')) {
                this.favoriteRequestTypeIds = normalized;
                this.persistFavoriteRequestTypes();
            }
        },
        isFavoriteRequestType(id) {
            return Boolean(this.favoriteRequestTypeSet[String(id)]);
        },
        toggleFavoriteRequestType(id) {
            const value = String(id || '');
            if (!value || value === 'all') return;

            if (this.isFavoriteRequestType(value)) {
                this.favoriteRequestTypeIds = this.favoriteRequestTypeIds.filter(item => String(item) !== value);
            } else {
                this.favoriteRequestTypeIds = this.normalizeFavoriteRequestTypeIds(this.favoriteRequestTypeIds.concat(value), false);
            }
            this.persistFavoriteRequestTypes();
            this.notifyLayoutChange();
        },
        stopFavoriteClick() {},
        scrollHorizontalOnWheel(event) {
            const target = event && event.currentTarget;
            if (!target || event.ctrlKey || target.scrollWidth <= target.clientWidth) return;

            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
                ? event.deltaX
                : event.deltaY;
            if (!delta) return;

            const previousScrollLeft = target.scrollLeft;
            target.scrollLeft += delta;
            if (target.scrollLeft !== previousScrollLeft) event.preventDefault();
        },
        isMobileViewport() {
            return typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
        },
        notifyLayoutChange() {
            this.$nextTick(() => this.$emit('layoutChange'));
        },
        toggleMobileFilters() {
            this.mobileFiltersOpen = !this.mobileFiltersOpen;
            this.notifyLayoutChange();
        },
        closeMobileFilters() {
            if (!this.isMobileViewport() || !this.mobileFiltersOpen) return;
            this.mobileFiltersOpen = false;
            this.notifyLayoutChange();
        },
        onFilterChange() {
            this.$emit('filterChange', this.localFilter);
        },
        onClearFilter() {
            this.localFilter = '';
            this.$emit('filterChange', '');
            this.closeMobileFilters();
        },
        onSortByChange() {
            this.$emit('sortByChange', this.localSortBy);
            this.closeMobileFilters();
        },
        toggleSortDesc() {
            this.localSortDesc = !this.localSortDesc;
            this.$emit('sortDescChange', this.localSortDesc);
            this.closeMobileFilters();
        },
        setStatusFilter(status) {
            this.$emit('statusFilterChange', status);
            this.closeMobileFilters();
        },
        setRequestTypeFilter(value) {
            this.localRequestTypeFilter = String(value || 'all');
            this.$emit('requestTypeFilterChange', this.localRequestTypeFilter);
            this.closeMobileFilters();
        },
        setBuildingProcessFilter(value) {
            this.localBuildingProcessFilter = String(value || 'all');
            this.$emit('buildingProcessFilterChange', this.localBuildingProcessFilter);
            this.closeMobileFilters();
        },
        setFavoriteRequestTypeFilter(value) {
            const normalizedValue = String(value || 'all');
            const nextValue = String(this.requestTypeFilter || 'all') === normalizedValue ? 'all' : normalizedValue;
            this.setRequestTypeFilter(nextValue);
        },
        onRequestTypeFilterChange(value) {
            this.setRequestTypeFilter(value);
        },
        onBuildingProcessFilterChange(value) {
            this.setBuildingProcessFilter(value);
        },
        clearFilterChip(key) {
            if (key === 'recurrence') {
                this.$emit('recurrenceFilterChange', '');
                return;
            }
            if (key === 'search') {
                this.onClearFilter();
            } else if (key === 'status') {
                this.setStatusFilter('all');
            } else if (key === 'requestType') {
                this.setRequestTypeFilter('all');
            } else if (key === 'buildingProcess') {
                this.setBuildingProcessFilter('all');
            }
        },
        clearAllFilters() {
            this.localFilter = '';
            this.localRequestTypeFilter = 'all';
            this.localBuildingProcessFilter = 'all';
            this.$emit('filtersReset');
            this.closeMobileFilters();
        },
        createRequest() {
            this.$router.push('/requests/create');
        }
    },
    mounted() {
        this.loadFavoriteRequestTypes();
    },
    watch: {
        filter(v) { this.localFilter = v; },
        sortBy(v) { this.localSortBy = v; },
        sortDesc(v) { this.localSortDesc = v; },
        requestTypeFilter(v) { this.localRequestTypeFilter = String(v || 'all'); },
        buildingProcessFilter(v) { this.localBuildingProcessFilter = String(v || 'all'); },
        favoriteStorageKey() { this.loadFavoriteRequestTypes(); },
        requestTypes() { this.pruneFavoriteRequestTypes(); }
    }
}
</script>

<style scoped>
.requests-navbar :deep(.card-body) {
    padding: .5rem .75rem;
}

.requests-toolbar {
    display: grid;
    grid-template-columns: auto minmax(160px, .9fr) minmax(150px, 220px) minmax(190px, 235px) auto;
    align-items: center;
    gap: .5rem;
}

.requests-toolbar--has-favorites {
    grid-template-columns: auto minmax(130px, 230px) minmax(90px, 1fr) minmax(150px, 210px) minmax(190px, 235px) auto;
}

.requests-toolbar__title {
    display: flex;
    align-items: center;
    gap: .5rem;
    grid-column: 1;
    grid-row: 1;
    flex: 0 0 auto;
}

.requests-toolbar__search {
    grid-column: 2;
    grid-row: 1;
    min-width: 160px;
    transition: min-width .18s ease;
}

.requests-toolbar--has-favorites .requests-toolbar__search {
    min-width: 130px;
}

.requests-toolbar__favorites {
    grid-column: 3;
    grid-row: 1;
    min-width: 0;
    overflow: hidden;
}

.requests-favorite-types {
    display: flex;
    align-items: center;
    gap: .3rem;
    min-width: 0;
    overflow-x: auto;
    padding-bottom: 1px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.requests-favorite-types::-webkit-scrollbar {
    display: none;
}

.requests-favorite-type {
    display: inline-flex;
    align-items: stretch;
    flex: 0 0 auto;
    max-width: 230px;
    min-height: 31px;
}

.requests-favorite-type__filter,
.requests-favorite-type__remove {
    display: inline-flex;
    align-items: center;
    border: 1px solid #9ec5fe;
    background: #e7f1ff;
    color: #084298;
    font-size: .8125rem;
    line-height: 1.2;
    transition: filter .15s ease, box-shadow .15s ease, transform .15s ease;
}

.requests-favorite-type__filter {
    gap: .25rem;
    min-width: 0;
    padding: .25rem .45rem .25rem .55rem;
    border-radius: 999px 0 0 999px;
    font-weight: 600;
}

.requests-favorite-type__filter span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-favorite-type__remove {
    flex: 0 0 auto;
    width: 30px;
    justify-content: center;
    padding: .25rem .35rem;
    border-left: 0;
    border-radius: 0 999px 999px 0;
    color: #b38300;
}

.requests-favorite-type__filter:hover,
.requests-favorite-type__remove:hover {
    filter: brightness(.96);
}

.requests-favorite-type--active .requests-favorite-type__filter,
.requests-favorite-type--active .requests-favorite-type__remove {
    background: #007bff;
    border-color: #007bff;
    color: #fff;
}

.requests-favorite-type--active {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, .25);
    border-radius: 999px;
    transform: translateY(-1px);
}

.requests-toolbar__type {
    grid-column: 4;
    grid-row: 1;
    min-width: 190px;
}

.requests-toolbar--has-favorites .requests-toolbar__type {
    grid-column: 5;
}

.requests-toolbar__type .requests-type-filter {
    width: 100%;
}

.requests-toolbar__object {
    grid-column: 3;
    grid-row: 1;
    min-width: 0;
}

.requests-toolbar--has-favorites .requests-toolbar__object {
    grid-column: 4;
}

.requests-toolbar__object .requests-object-filter {
    width: 100%;
}

.requests-toolbar__sort {
    display: flex;
    align-items: center;
    gap: .35rem;
    flex: 0 0 245px;
    min-width: 205px;
}

.requests-toolbar__recurrence {
    flex: 0 0 185px;
    width: 185px;
}

.requests-toolbar__sort .custom-select {
    min-width: 0;
}

.requests-toolbar__filters {
    display: flex;
    align-items: center;
    gap: .4rem;
    grid-column: 1 / -1;
    grid-row: 2;
    min-width: 0;
}

.requests-filter-chips {
    display: flex;
    align-items: center;
    gap: .3rem;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    padding-bottom: 1px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.requests-filter-chips::-webkit-scrollbar {
    display: none;
}

.requests-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: .25rem;
    flex: 0 0 auto;
    min-height: 31px;
    padding: .25rem .55rem;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: .8125rem;
    line-height: 1.2;
    white-space: nowrap;
    color: #495057;
    transition: box-shadow .15s ease, transform .15s ease, filter .15s ease;
}

.requests-filter-chip:hover {
    filter: brightness(.96);
}

.requests-filter-chip--active {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, .18);
    transform: translateY(-1px);
    color: #fff;
}

.requests-filter-chip__check {
    font-size: .72rem;
}

.requests-filter-chip--secondary {
    background: #f1f3f5;
    border-color: #adb5bd;
    color: #495057;
}

.requests-filter-chip--danger {
    background: #fbe9eb;
    border-color: #f1aeb5;
    color: #842029;
}

.requests-filter-chip--warning {
    background: #fff3cd;
    border-color: #ffda6a;
    color: #664d03;
}

.requests-filter-chip--primary {
    background: #e7f1ff;
    border-color: #9ec5fe;
    color: #084298;
}

.requests-filter-chip--info {
    background: #cff4fc;
    border-color: #9eeaf9;
    color: #055160;
}

.requests-filter-chip--success {
    background: #d1e7dd;
    border-color: #a3cfbb;
    color: #0f5132;
}

.requests-filter-chip--dark {
    background: #e9ecef;
    border-color: #adb5bd;
    color: #212529;
}

.requests-filter-chip--light {
    background: #f8f9fa;
    border-color: #dee2e6;
    color: #212529;
}

.requests-filter-chip--secondary.requests-filter-chip--active {
    background: #6c757d;
    border-color: #6c757d;
}

.requests-filter-chip--danger.requests-filter-chip--active {
    background: #dc3545;
    border-color: #dc3545;
}

.requests-filter-chip--warning.requests-filter-chip--active {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.requests-filter-chip--primary.requests-filter-chip--active {
    background: #007bff;
    border-color: #007bff;
}

.requests-filter-chip--info.requests-filter-chip--active {
    background: #17a2b8;
    border-color: #17a2b8;
}

.requests-filter-chip--success.requests-filter-chip--active {
    background: #28a745;
    border-color: #28a745;
}

.requests-filter-chip--dark.requests-filter-chip--active {
    background: #343a40;
    border-color: #343a40;
}

.requests-filter-chip--light.requests-filter-chip--active {
    background: #f8f9fa;
    border-color: #d6d8db;
    color: #212529;
}

.requests-type-filter {
    flex: 0 0 240px;
    min-width: 190px;
    font-size: .875rem;
}

.requests-type-filter :deep(.vs__dropdown-toggle) {
    min-height: 31px;
    padding: 0 .25rem 1px;
    border: 1px solid #ced4da;
    border-radius: .2rem;
    background: #fff;
}

.requests-type-filter :deep(.vs__selected-options) {
    flex-wrap: nowrap;
    min-width: 0;
}

.requests-type-filter :deep(.vs__selected) {
    max-width: 175px;
    margin: 2px 2px 0;
    color: #495057;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-type-filter :deep(.vs__search) {
    margin: 2px 0 0;
    padding: 0;
    font-size: .875rem;
    line-height: 1.4;
}

.requests-type-filter :deep(.vs__actions) {
    padding: 0 2px 0 4px;
}

.requests-type-filter :deep(.vs__dropdown-menu) {
    font-size: .875rem;
}

.requests-type-filter__category {
    display: flex;
    align-items: center;
    gap: .4rem;
    margin: .2rem -.4rem .15rem;
    padding: .35rem .65rem .25rem;
    border-top: 1px solid #e9ecef;
    color: #495057;
    background: #f8f9fa;
    font-size: .78rem;
    font-weight: 700;
    line-height: 1.15;
    text-transform: uppercase;
}

.requests-type-filter__category-icon {
    width: 1rem;
    color: #6c757d;
    text-align: center;
}

.requests-type-filter__option {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: .4rem;
    min-width: 0;
}

.requests-type-filter__option-text {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-type-filter :deep(.vs__dropdown-option--disabled) {
    padding-top: 0;
    padding-bottom: 0;
    color: inherit;
    background: transparent;
    cursor: default;
}

.requests-type-filter :deep(.vs__dropdown-option--disabled.vs__dropdown-option--highlight) {
    color: inherit;
    background: transparent;
}

.requests-type-filter__favorite {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 28px;
    height: 26px;
    border: 0;
    background: transparent;
    color: #adb5bd;
}

.requests-type-filter__favorite:hover,
.requests-type-filter__favorite--active {
    color: #b38300;
}

.requests-type-filter__selected {
    display: inline-flex;
    align-items: center;
    gap: .25rem;
    min-width: 0;
}

.requests-type-filter__selected-star {
    color: #b38300;
    font-size: .75rem;
}

.requests-type-filter__selected-category-icon {
    color: #6c757d;
    font-size: .8rem;
}

.requests-type-filter--active :deep(.vs__dropdown-toggle) {
    border-color: #ffc107;
    box-shadow: 0 0 0 2px rgba(255, 193, 7, .35);
    background-color: #fff8e1;
}

.requests-type-filter--active :deep(.vs__selected) {
    font-weight: 600;
}

.requests-object-filter {
    flex: 0 0 220px;
    min-width: 0;
    max-width: 100%;
    font-size: .875rem;
}

.requests-object-filter :deep(.vs__dropdown-toggle) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    min-height: 31px;
    padding: 0 .25rem 1px;
    border: 1px solid #ced4da;
    border-radius: .2rem;
    background: #fff;
    overflow: hidden;
}

.requests-object-filter :deep(.vs__selected-options) {
    flex: 1 1 0;
    flex-wrap: nowrap;
    width: 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.requests-object-filter :deep(.vs__selected) {
    display: block;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    margin: 2px 2px 0;
    color: #495057;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-object-filter :deep(.vs--single .vs__selected),
.requests-object-filter :deep(.vs__selected-options .vs__selected) {
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.requests-object-filter :deep(.vs__search) {
    width: 0;
    min-width: 0;
    margin: 2px 0 0;
    padding: 0;
    font-size: .875rem;
    line-height: 1.4;
}

.requests-object-filter :deep(.vs__actions) {
    flex: 0 0 auto;
    padding: 0 2px 0 4px;
}

.requests-object-filter :deep(.vs__dropdown-menu) {
    font-size: .875rem;
}

.requests-object-filter__selected {
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-object-filter--active :deep(.vs__dropdown-toggle) {
    border-color: #17a2b8;
    box-shadow: 0 0 0 2px rgba(23, 162, 184, .28);
    background-color: #e8f7fa;
}

.requests-object-filter--active :deep(.vs__selected) {
    font-weight: 600;
}

.requests-active-filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .35rem;
    grid-column: 1 / -1;
    grid-row: 3;
    padding: .4rem .5rem;
    border: 1px solid #ffe08a;
    border-radius: .35rem;
    background: #fff8e1;
    color: #5c4300;
    font-size: .84rem;
}

.requests-active-filters__label {
    display: inline-flex;
    align-items: center;
    gap: .3rem;
    flex: 0 0 auto;
    font-weight: 700;
}

.requests-active-filter {
    display: inline-flex;
    align-items: center;
    gap: .25rem;
    max-width: 100%;
    min-height: 28px;
    padding: .2rem .45rem;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    font-size: .8rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.requests-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-active-filter__remove {
    margin-left: .1rem;
    opacity: .85;
}

.requests-active-filter--primary {
    background: #007bff;
    border-color: #007bff;
}

.requests-active-filter--secondary {
    background: #6c757d;
    border-color: #6c757d;
}

.requests-active-filter--danger {
    background: #dc3545;
    border-color: #dc3545;
}

.requests-active-filter--warning {
    background: #ffc107;
    border-color: #ffc107;
    color: #212529;
}

.requests-active-filter--info {
    background: #17a2b8;
    border-color: #17a2b8;
}

.requests-active-filter--success {
    background: #28a745;
    border-color: #28a745;
}

.requests-active-filter--dark {
    background: #343a40;
    border-color: #343a40;
}

.requests-active-filter--light {
    background: #f8f9fa;
    border-color: #d6d8db;
    color: #212529;
}

.requests-active-filters__reset {
    flex: 0 0 auto;
    min-height: 28px;
    padding: .2rem .5rem;
    border: 1px solid #b38300;
    border-radius: .25rem;
    background: #fff;
    color: #5c4300;
    font-size: .8rem;
    font-weight: 700;
    line-height: 1.2;
}

.requests-toolbar__actions {
    display: flex;
    align-items: center;
    gap: .35rem;
    grid-column: 5;
    grid-row: 1;
    justify-self: end;
}

.requests-toolbar--has-favorites .requests-toolbar__actions {
    grid-column: 6;
}

.create-btn {
    flex: 0 0 auto;
    min-width: 136px;
    padding-left: .5rem;
    padding-right: .5rem;
    white-space: nowrap;
}

.request-types-btn {
    flex: 0 0 auto;
    min-width: 112px;
    padding-left: .5rem;
    padding-right: .5rem;
    white-space: nowrap;
}

.request-types-btn i {
    margin-right: .25rem;
}

.sort-dir-btn {
    min-width: 36px;
    flex-shrink: 0;
}

.filters-toggle {
    min-width: 34px;
    position: relative;
}

.filters-toggle--active {
    color: #007bff;
    border-color: #007bff;
}

.filters-toggle__dot {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #dc3545;
    box-shadow: 0 0 0 1px #fff;
}

@media (max-width: 767.98px) {
    .requests-navbar :deep(.card-body) {
        padding: .15rem .3rem .25rem;
    }

    .requests-navbar {
        margin-bottom: .2rem !important;
        border-radius: .2rem;
    }

    .requests-toolbar {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        column-gap: .25rem;
        row-gap: .14rem;
    }

    .requests-toolbar__title {
        grid-column: 1 / 2;
        min-width: 0;
    }

    .requests-toolbar__title h5 {
        font-size: 1rem;
    }

    .requests-toolbar__actions {
        grid-column: 2 / 3;
        grid-row: 1;
        justify-self: end;
    }

    .request-types-btn {
        min-width: 0;
        padding-left: .45rem;
        padding-right: .45rem;
    }

    .requests-toolbar__search {
        grid-column: 1 / 3;
        grid-row: 2;
        display: flex;
        min-width: 0;
        width: 100%;
    }

    .requests-toolbar--has-favorites {
        grid-template-columns: 1fr auto;
    }

    .requests-toolbar--has-favorites .requests-toolbar__search {
        min-width: 0;
    }

    .requests-toolbar__favorites {
        grid-column: 1 / 3;
        grid-row: 3;
        width: 100%;
    }

    .requests-favorite-types {
        gap: .2rem;
    }

    .requests-favorite-type {
        max-width: min(260px, 78vw);
        min-height: 29px;
    }

    .requests-favorite-type__filter {
        padding: .2rem .38rem .2rem .45rem;
    }

    .requests-favorite-type__remove {
        width: 28px;
        padding: .2rem .3rem;
    }

    .requests-toolbar__sort {
        display: none;
        flex: 0 0 auto;
        min-width: 0;
        margin-left: 0;
        width: 100%;
    }

    .requests-toolbar__recurrence {
        flex: 0 0 auto;
        width: 100%;
    }

    .requests-toolbar__type {
        grid-column: 1 / 3;
        grid-row: 6;
        display: none;
        min-width: 0;
        width: 100%;
    }

    .requests-toolbar__object {
        grid-column: 1 / 3;
        grid-row: 5;
        display: none;
        min-width: 0;
        width: 100%;
    }

    .requests-toolbar__filters {
        grid-column: 1 / 3;
        grid-row: 4;
        display: none;
        width: 100%;
        flex-direction: column;
        align-items: stretch;
    }

    .requests-active-filters {
        grid-column: 1 / 3;
        grid-row: 3;
        align-items: flex-start;
        gap: .3rem;
        padding: .25rem .35rem;
    }

    .requests-active-filters__label,
    .requests-active-filters__reset {
        width: 100%;
    }

    .requests-active-filter {
        max-width: 100%;
        white-space: normal;
        text-align: left;
    }

    .requests-toolbar--filters-open .requests-toolbar__sort,
    .requests-toolbar--filters-open .requests-toolbar__object,
    .requests-toolbar--filters-open .requests-toolbar__type,
    .requests-toolbar--filters-open .requests-toolbar__filters {
        display: flex;
    }

    .requests-toolbar--has-favorites .requests-active-filters {
        grid-row: 4;
    }

    .requests-toolbar--has-favorites .requests-toolbar__filters {
        grid-row: 5;
    }

    .requests-toolbar--has-favorites .requests-toolbar__type {
        grid-column: 1 / 3;
        grid-row: 7;
    }

    .requests-toolbar--has-favorites .requests-toolbar__object {
        grid-column: 1 / 3;
        grid-row: 6;
    }

    .requests-toolbar--has-favorites .requests-toolbar__actions {
        grid-column: 2 / 3;
    }

    .requests-filter-chips {
        width: 100%;
    }

    .requests-type-filter {
        flex: 0 0 auto;
        width: 100%;
        min-width: 0;
    }

    .requests-object-filter {
        flex: 0 0 auto;
        width: 100%;
        min-width: 0;
    }

    .requests-type-filter :deep(.vs__selected) {
        max-width: calc(100vw - 110px);
    }

    .requests-object-filter :deep(.vs__selected) {
        max-width: 100%;
    }

    .create-btn {
        margin-left: 0;
        min-width: 0;
        padding-left: .4rem;
        padding-right: .4rem;
        white-space: nowrap;
    }
}
</style>

<style>
.requests-navbar__body {
    padding: .5rem .75rem !important;
}

.requests-navbar .requests-object-filter {
    min-width: 0;
    max-width: 100%;
}

.requests-navbar .requests-object-filter .vs__dropdown-toggle {
    display: flex;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.requests-navbar .requests-object-filter .vs__selected-options {
    flex: 1 1 0 !important;
    flex-wrap: nowrap;
    width: 0;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
}

.requests-navbar .requests-object-filter .vs__selected {
    display: block;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-navbar .requests-object-filter__selected {
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.requests-navbar .requests-object-filter .vs__search {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    max-width: 100%;
}

.requests-navbar .requests-object-filter.vs--open .vs__search,
.requests-navbar .requests-object-filter.vs--searching .vs__search {
    flex: 1 1 auto;
    min-width: 4rem;
}

.requests-navbar .requests-object-filter.vs--open .vs__selected,
.requests-navbar .requests-object-filter.vs--searching .vs__selected {
    max-width: 0;
    margin-left: 0;
    margin-right: 0;
    padding-left: 0;
    padding-right: 0;
    opacity: 0;
}

.requests-navbar .requests-object-filter .vs__actions {
    flex: 0 0 auto;
}

@media (max-width: 767.98px) {
    .requests-navbar {
        margin-bottom: .2rem !important;
    }

    .requests-navbar__body {
        padding: .28rem .35rem .32rem !important;
    }

    .requests-navbar .requests-toolbar__title h5 {
        font-size: .95rem;
        line-height: 1.15;
    }

    .requests-navbar .btn-sm,
    .requests-navbar .input-group-sm > .form-control,
    .requests-navbar .input-group-sm > .input-group-prepend > .input-group-text,
    .requests-navbar .input-group-sm > .input-group-append > .btn {
        min-height: 29px;
        height: 29px;
        padding-top: .15rem;
        padding-bottom: .15rem;
    }
}
</style>
